import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { highnote } from "@highnote/server/src/sdk";
import type {
  DiscoverPageItemDetail,
  DiscoverPageItems,
} from "@highnote/server/src/functions/handlers/discover/types";

/**
 * Per-recordId cache entry. Loading flag and data live together so they
 * can't drift out of sync. `data` is `null` while in-flight or on error;
 * `loading` is the unambiguous signal that a request is still outstanding.
 */
export type DiscoverPageItemDetailEntry = {
  loading: boolean;
  data: DiscoverPageItemDetail | null;
};

type DiscoverPageItemsContextValue = {
  items: DiscoverPageItems | null;
  loading: boolean;
  error: Error | null;
  loadItems: () => void;
  /**
   * Per-recordId cache of item details. Lookups are referentially stable:
   * when one entry updates, the other entries' references are preserved by
   * the spread in the setter, so parent components passing
   * `itemDetails[id]` as a prop to memoized children avoid re-rendering
   * unrelated children.
   */
  itemDetails: Record<string, DiscoverPageItemDetailEntry>;
  loadItemDetail: (recordId: string) => void;
};

const DiscoverPageItemsContext = createContext<DiscoverPageItemsContextValue>({
  items: null,
  loading: false,
  error: null,
  loadItems: () => undefined,
  itemDetails: {},
  loadItemDetail: () => undefined,
});

/**
 * Caches the discover page items (sections + recordIds) AND per-item
 * details for the lifetime of the authenticated app. Items are fetched
 * once on first /home visit; per-item details are fetched lazily as
 * consumers (tiles, search) request them via `loadItemDetail(recordId)`.
 *
 * Mounted in `Main.tsx` above the route Switch so caches survive
 * navigation. Both fetches are lazy: nothing hits Airtable until a
 * consumer explicitly asks.
 */
export const DiscoverPageItemsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [items, setItems] = useState<DiscoverPageItems | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const fetchedRef = useRef(false);

  const [itemDetails, setItemDetails] = useState<
    Record<string, DiscoverPageItemDetailEntry>
  >({});

  const loadItemDetail = useCallback((recordId: string) => {
    let shouldFetch = false;
    setItemDetails((prev) => {
      if (prev[recordId]) return prev;
      shouldFetch = true;
      return { ...prev, [recordId]: { loading: true, data: null } };
    });
    if (!shouldFetch) return;

    highnote
      .getDiscoverPageItemDetail(recordId)
      .catch((): DiscoverPageItemDetail | null => null)
      .then((result) => {
        setItemDetails((prev) => ({
          ...prev,
          [recordId]: { loading: false, data: result },
        }));
      });
  }, []);

  const loadItems = useCallback(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    setLoading(true);
    highnote
      .getDiscoverPageItems()
      .then((result) => {
        setItems(result);
        // Cascade: kick off detail fetches for every item in the index.
        // Consumers (tiles, search) can read from `itemDetails` directly
        // without having to schedule fetches themselves.
        result.sections.forEach((section) => {
          section.items.forEach((item) => loadItemDetail(item.recordId));
          section.tabs?.forEach((tab) => {
            tab.items.forEach((item) => loadItemDetail(item.recordId));
          });
        });
      })
      .catch(setError)
      .finally(() => setLoading(false));
  }, [loadItemDetail]);

  const value = useMemo(
    () => ({
      items,
      loading,
      error,
      loadItems,
      itemDetails,
      loadItemDetail,
    }),
    [items, loading, error, loadItems, itemDetails, loadItemDetail],
  );

  return (
    <DiscoverPageItemsContext.Provider value={value}>
      {children}
    </DiscoverPageItemsContext.Provider>
  );
};

/**
 * Reads the cached discover page items + the `loadItems` trigger from
 * context.
 *
 * Pure read — does NOT auto-fetch. Callers are expected to explicitly invoke
 * `loadItems()` (typically from a `useEffect` on mount) so the trigger is
 * visible at the call site. The provider dedupes, so calling `loadItems()`
 * multiple times across mounts is safe.
 */
export const useDiscoverPageItems = () => useContext(DiscoverPageItemsContext);

/**
 * Single-item-detail hook for callers that don't need the full provider
 * surface. Triggers a fetch on mount; returns the cached entry from
 * provider state.
 *
 * For lists that render many details, prefer lifting the read into the
 * parent and passing `itemDetails[id]` as a prop to memoized children —
 * that way unrelated detail updates don't re-render every list item.
 */
export const useDiscoverPageItemDetail = (recordId: string) => {
  const { itemDetails, loadItemDetail } = useDiscoverPageItems();

  useEffect(() => {
    loadItemDetail(recordId);
  }, [recordId, loadItemDetail]);

  const entry = itemDetails[recordId];
  return {
    itemDetail: entry?.data ?? null,
    loading: !entry || entry.loading,
  };
};
